import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRoute } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Loader2, CheckCircle2, AlertTriangle, Clock, Lock, XCircle,
  ChevronDown, ChevronUp, Send,
} from "lucide-react";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { formatQtySnapshot } from "@shared/format-qty";
import { poBrandLabel } from "@shared/purchase-order-brands";
import { RecordReloadReview } from "@/components/RecordReloadReview";
import { createReloadMerge, resolveReloadMerge, type ReloadChoices, type ReloadMergePlan, type ReloadValues } from "@/lib/recordReloadMerge";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(val: any) {
  if (!val) return "—";
  try { return format(parseISO(typeof val === "string" ? val : new Date(val).toISOString()), "dd/MM/yyyy"); }
  catch { return String(val); }
}
function fmtDateLong(val: any) {
  if (!val) return "—";
  try { return format(parseISO(typeof val === "string" ? val : new Date(val).toISOString()), "EEEE, dd MMM yyyy"); }
  catch { return String(val); }
}
function expiryText(val: any, t: (key: string, opts?: any) => string) {
  if (!val) return "";
  try {
    const d = parseISO(typeof val === "string" ? val : new Date(val).toISOString());
    const ms = d.getTime() - Date.now();
    if (ms <= 0) return t("seg18.expired");
    return t("seg18.expiresIn", { time: formatDistanceToNow(d, { addSuffix: true }) });
  } catch { return ""; }
}

const BRANCH_STATUS_BORDER: Record<string, string> = {
  submitted_confirmed: "border-s-green-500",
  submitted_adjusted: "border-s-amber-400",
  submitted_issue: "border-s-red-500",
  submitted: "border-s-indigo-500",
  skipped: "border-s-gray-300",
  pending: "border-s-yellow-400",
};

const RESPONSE_CONFIG: Record<string, { bg: string; icon: React.ReactElement }> = {
  confirmed: {
    bg: "bg-green-50 border border-green-200 text-green-800",
    icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
  },
  adjusted: {
    bg: "bg-amber-50 border border-amber-200 text-amber-800",
    icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
  },
  issue: {
    bg: "bg-red-50 border border-red-200 text-red-800",
    icon: <XCircle className="h-4 w-4 text-red-600" />,
  },
};

// ─── Branch Card ──────────────────────────────────────────────────────────────

function BranchCard({ branch: initialBranch, showPricing, onRespond, onReload }: {
  branch: any;
  showPricing: boolean;
  onRespond: (poId: string, status: string, comment: string, adjustedLines: any[] | undefined, expectedRevision: number) => Promise<void>;
  onReload: (poId: string) => Promise<any>;
}) {
  const { t } = useTranslation();
  const [branch, setBranch] = useState(initialBranch);
  const [mode, setMode] = useState<"idle" | "adjust" | "issue">("idle");
  const [adjustQtys, setAdjustQtys] = useState<Record<string, string>>({});
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localResponse, setLocalResponse] = useState<any>(initialBranch.response ?? null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [review, setReview] = useState<{ latest: any; plan: ReloadMergePlan } | null>(null);
  const [choices, setChoices] = useState<ReloadChoices>({});
  const blocked = submitting || reloading || conflict || !!review || !!localResponse;
  const snapshot = (record: any): ReloadValues => ({
    comment: record.response?.comment ?? "",
    response: record.response?.status ?? null,
    ...Object.fromEntries((record.items ?? []).map((item: any) => [`quantity:${item.poLineId}`, Number(item.orderedQty)])),
    ...Object.fromEntries((record.items ?? []).map((item: any) => [`item:${item.poLineId}`, { name: item.itemName, brandId: item.brandId ?? null, brandName: item.brandName, brandNameAr: item.brandNameAr, unit: item.unit, notes: item.notes ?? null, unitCost: item.unitCost ?? null }])),
  });
  const reload = async () => {
    setReloading(true);
    try {
      const latest = await onReload(branch.poId);
      const base = snapshot(branch);
      const mine: ReloadValues = { ...base, comment };
      if (mode === "adjust") for (const item of branch.items ?? []) {
        mine[`quantity:${item.poLineId}`] = Number(adjustQtys[item.poLineId] ?? item.orderedQty);
      }
      setReview({ latest, plan: createReloadMerge(base, mine, snapshot(latest)) });
      setChoices({});
      setError(null);
    } catch (e: any) { setError(e.message ?? t("seg18.somethingWrong")); }
    finally { setReloading(false); }
  };
  const applyReload = () => {
    if (!review) return;
    const merged = resolveReloadMerge(review.plan, choices);
    const ids = new Set((review.latest.items ?? []).map((item: any) => String(item.poLineId)));
    if (Object.keys(merged).some(key => key.startsWith("quantity:") && !ids.has(key.slice(9)) && merged[key] !== undefined)) {
      setError("This item was removed from the order. Choose its latest saved value before continuing.");
      return;
    }
    setAdjustQtys(Object.fromEntries((review.latest.items ?? []).map((item: any) => [item.poLineId, String(merged[`quantity:${item.poLineId}`] ?? item.orderedQty)])));
    setComment(String(merged.comment ?? ""));
    setBranch(review.latest);
    setLocalResponse(review.latest.response ?? null);
    setReview(null);
    setConflict(false);
    setError(null);
  };

  const response = localResponse;
  const items: any[] = branch.items ?? [];

  const borderClass = (() => {
    if (branch.status === "skipped") return BRANCH_STATUS_BORDER.skipped;
    if (branch.status === "pending") return BRANCH_STATUS_BORDER.pending;
    if (response?.status === "confirmed") return BRANCH_STATUS_BORDER.submitted_confirmed;
    if (response?.status === "adjusted") return BRANCH_STATUS_BORDER.submitted_adjusted;
    if (response?.status === "issue") return BRANCH_STATUS_BORDER.submitted_issue;
    return BRANCH_STATUS_BORDER.submitted;
  })();

  const handleConfirm = async () => {
    if (!branch.poId || blocked) return;
    setSubmitting(true);
    setError(null);
    try {
      await onRespond(branch.poId, "confirmed", "", undefined, branch.revision);
      setLocalResponse({ status: "confirmed", comment: "" });
    } catch (e: any) {
      if (e.status === 409) setConflict(true);
      setError(e.message ?? t("seg18.somethingWrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdjustSubmit = async () => {
    if (blocked) return;
    if (!comment.trim()) { setError(t("seg18.reasonRequired")); return; }
    const lines = items.map(item => ({
      poLineId: item.poLineId,
      adjustedQty: parseFloat(adjustQtys[item.poLineId] ?? String(item.orderedQty)) || 0,
    }));
    setSubmitting(true);
    setError(null);
    try {
      await onRespond(branch.poId, "adjusted", comment, lines, branch.revision);
      setLocalResponse({ status: "adjusted", comment, adjustedLines: lines });
      setMode("idle");
    } catch (e: any) {
      if (e.status === 409) setConflict(true);
      setError(e.message ?? t("seg18.somethingWrong"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleIssueSubmit = async () => {
    if (blocked) return;
    if (!comment.trim()) { setError(t("seg18.issueRequired")); return; }
    setSubmitting(true);
    setError(null);
    try {
      await onRespond(branch.poId, "issue", comment, undefined, branch.revision);
      setLocalResponse({ status: "issue", comment });
      setMode("idle");
    } catch (e: any) {
      if (e.status === 409) setConflict(true);
      setError(e.message ?? t("seg18.somethingWrong"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className={`border-s-[3px] ${borderClass} shadow-sm`}>
      <CardContent className="p-4 space-y-3">
        {/* Branch header */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="font-semibold text-base" dir="auto">{branch.branchName}</p>
          {branch.status === "skipped" && (
            <Badge variant="outline" className="uppercase text-[10px] font-bold border bg-gray-100 text-gray-600 border-gray-200">{t("seg18.skipped")}</Badge>
          )}
          {branch.status === "pending" && (
            <span className="text-sm text-amber-600 flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />{t("seg18.notYetSubmitted")}
            </span>
          )}
          {["submitted", "late"].includes(branch.status) && !response && (
            <Badge variant="outline" className="uppercase text-[10px] font-bold border bg-indigo-100 text-indigo-800 border-indigo-200">{t("seg18.submitted")}</Badge>
          )}
          {["submitted", "late"].includes(branch.status) && response && (
            <Badge variant="outline" className={`uppercase text-[10px] font-bold border ${
              response.status === "confirmed" ? "bg-green-100 text-green-800 border-green-200" :
              response.status === "adjusted" ? "bg-amber-100 text-amber-800 border-amber-200" :
              "bg-red-100 text-red-800 border-red-200"
            }`}>
              {t(`seg18.status_${response.status}`, { defaultValue: response.status })}
            </Badge>
          )}
        </div>

        {/* Skipped note */}
        {branch.status === "skipped" && (
          <p className="text-sm text-muted-foreground italic" dir="auto">
            {branch.note ?? t("seg18.noOrderThisWeek")}
          </p>
        )}

        {/* Pending */}
        {branch.status === "pending" && null}

        {/* Submitted branch content */}
        {["submitted", "late"].includes(branch.status) && (
          <>
            {/* Items table */}
            {items.length > 0 && (
              <div className="rounded-md border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60">
                    <tr>
                      <th className="text-start px-3 py-2 font-medium" dir="auto">{t("seg18.item")}</th>
                      <th className="text-end px-3 py-2 font-medium">{t("seg18.qty")}</th>
                      <th className="text-end px-3 py-2 font-medium text-muted-foreground">{t("seg18.unit")}</th>
                      {showPricing && <th className="text-end px-3 py-2 font-medium">{t("seg18.cost")}</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {items.map((item: any) => (
                      <tr key={item.poLineId ?? item.itemName}>
                        <td className="px-3 py-2" dir="auto">
                          {item.itemName}
                          {poBrandLabel(item) && <small className="block" dir="auto">{poBrandLabel(item)}</small>}
                          {item.notes && (
                            <span className="block text-xs text-blue-700 dark:text-blue-400 mt-0.5" dir="auto" data-testid={`text-line-note-${item.poLineId ?? item.itemName}`}>
                              <span className="font-medium">{t("seg18.note")}:</span> {item.notes}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-end font-mono font-bold">{item.orderedQty}</td>
                        <td className="px-3 py-2 text-end text-muted-foreground">{item.unit}</td>
                        {showPricing && (
                          <td className="px-3 py-2 text-end font-mono">
                            {item.unitCost != null ? `${Number(item.lineCost ?? 0).toFixed(2)} EGP` : "—"}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Note */}
            {branch.note && (
              <p className="text-sm italic text-muted-foreground" dir="auto">{branch.note}</p>
            )}

            {/* Response locked state */}
            {response && RESPONSE_CONFIG[response.status] && (
              <div className={`flex items-start gap-2 p-3 rounded-md text-sm font-medium ${RESPONSE_CONFIG[response.status].bg}`}>
                {RESPONSE_CONFIG[response.status].icon}
                <div className="space-y-1 min-w-0">
                  <p>{t(`seg18.response_${response.status}`)}</p>
                  {response.comment && (
                    <p className="font-normal text-sm opacity-80" dir="auto">{response.comment}</p>
                  )}
                </div>
              </div>
            )}

            {/* Action buttons — only show if no response yet */}
            {!response && mode === "idle" && (
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  className="flex-1 min-w-[120px] h-11 gap-1.5 text-sm"
                  onClick={handleConfirm}
                  disabled={blocked}
                  data-testid={`button-confirm-${branch.branchId}`}
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {t("seg18.confirmBtn")}
                </Button>
                <Button variant="outline" className="flex-1 min-w-[120px] h-11 gap-1.5 text-sm"
                  disabled={blocked}
                  onClick={() => {
                    setAdjustQtys({});
                    setComment("");
                    setError(null);
                    setMode("adjust");
                  }}
                  data-testid={`button-adjust-${branch.branchId}`}
                >
                  {t("seg18.adjustQuantities")}
                </Button>
                <Button variant="outline" className="flex-1 min-w-[120px] h-11 gap-1.5 text-sm text-red-600 border-red-200 hover:text-red-700 hover:border-red-300 hover:bg-red-50"
                  disabled={blocked}
                  onClick={() => { setComment(""); setError(null); setMode("issue"); }}
                  data-testid={`button-issue-${branch.branchId}`}
                >
                  {t("seg18.reportIssue")}
                </Button>
              </div>
            )}

            {/* Error message */}
            {error && (
              <p className="text-sm text-red-600 font-medium" dir="auto">{error}</p>
            )}
            {conflict && !review && <Button variant="outline" disabled={reloading} onClick={reload}>Reload</Button>}
            {review && <RecordReloadReview plan={review.plan} choices={choices}
              labels={Object.fromEntries([...new Set([...Object.keys(review.plan.base), ...Object.keys(review.plan.latest)])].map(key => {
                const item = [...(branch.items ?? []), ...(review.latest.items ?? [])].find((entry: any) => String(entry.poLineId) === key.split(":")[1]);
                return [key, key === "comment" ? t("seg18.reasonLabel") : key === "response" ? "Supplier response" : `${item?.itemName ?? key} — ${key.startsWith("quantity:") ? t("seg18.qty") : t("seg18.item")}`];
              }))}
              formatValue={(value, key) => key.startsWith("item:") && value
                ? `${(value as any).name} · ${(value as any).unit}${(value as any).notes ? ` · ${(value as any).notes}` : ""}${(value as any).unitCost != null ? ` · ${(value as any).unitCost}` : ""}`
                : value == null ? "Not set" : String(value)}
              onChoice={(key, choice) => setChoices(previous => ({ ...previous, [key]: choice }))}
              onApply={applyReload} onCancel={() => setReview(null)} />}

            {/* Adjust mode */}
            {mode === "adjust" && (
              <div className="space-y-3 border rounded-md p-3 bg-amber-50/60">
                <p className="text-sm font-semibold text-amber-800">{t("seg18.adjustQuantitiesTitle")}</p>
                <div className="space-y-2">
                  {items.map((item: any) => (
                    <div key={item.poLineId} className="flex items-center gap-3">
                      <span className="flex-1 text-sm" dir="auto">{item.itemName}{poBrandLabel(item) && <small className="block">{poBrandLabel(item)}</small>}</span>
                      <span className="text-xs text-muted-foreground">{formatQtySnapshot(item.orderedQty, item.unit)}</span>
                      <Input
                        disabled={reloading || !!review}
                        type="number"
                        inputMode="numeric"
                        min={0}
                        className="w-24 text-end font-mono h-9"
                        placeholder={String(item.orderedQty)}
                        value={adjustQtys[item.poLineId] ?? ""}
                        onChange={e => setAdjustQtys(p => ({ ...p, [item.poLineId]: e.target.value }))}
                        data-testid={`input-adj-${item.poLineId}`}
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <Label className="text-sm">{t("seg18.reasonLabel")}</Label>
                  <Textarea
                    disabled={reloading || !!review}
                    rows={2}
                    className="mt-1"
                    placeholder={t("seg18.reasonPlaceholder")}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    dir="auto"
                    data-testid="input-adjust-comment"
                  />
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1 h-11" onClick={handleAdjustSubmit} disabled={blocked}
                    data-testid="button-submit-adjustment">
                    {submitting && <Loader2 className="h-4 w-4 animate-spin me-2" />}
                    {t("seg18.submitAdjustment")}
                  </Button>
                  <Button variant="ghost" className="h-11" disabled={conflict || reloading || !!review} onClick={() => { setMode("idle"); setError(null); }}>
                    {t("common.cancel")}
                  </Button>
                </div>
              </div>
            )}

            {/* Issue mode */}
            {mode === "issue" && (
              <div className="space-y-3 border rounded-md p-3 bg-red-50/60">
                <p className="text-sm font-semibold text-red-800">{t("seg18.reportIssueTitle")}</p>
                <div>
                  <Label className="text-sm">{t("seg18.describeIssue")}</Label>
                  <Textarea
                    disabled={reloading || !!review}
                    rows={3}
                    className="mt-1"
                    placeholder={t("seg18.issuePlaceholder")}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    dir="auto"
                    data-testid="input-issue-comment"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="destructive" className="flex-1 h-11" onClick={handleIssueSubmit} disabled={blocked}
                    data-testid="button-submit-issue">
                    {submitting && <Loader2 className="h-4 w-4 animate-spin me-2" />}
                    {t("seg18.submitIssue")}
                  </Button>
                  <Button variant="ghost" className="h-11" disabled={conflict || reloading || !!review} onClick={() => { setMode("idle"); setError(null); }}>
                    {t("common.cancel")}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main Portal Page ─────────────────────────────────────────────────────────

export default function SupplierPortal() {
  const { t } = useTranslation();
  const [, params] = useRoute("/s/:token");
  const token = params?.token ?? "";

  const [status, setStatus] = useState<"loading" | "error" | "ok">("loading");
  const [errorType, setErrorType] = useState<"invalid" | "expired" | "revoked" | "unknown">("unknown");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [data, setData] = useState<any>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    setData(null);
    if (!token) { setStatus("error"); setErrorType("invalid"); return; }
    fetch(`/api/public/supplier-portal/${token}`, { cache: "no-store", signal: controller.signal })
      .then(async r => {
        if (r.ok) {
          const d = await r.json();
          if (controller.signal.aborted) return;
          setData(d); setStatus("ok");
        }
        else {
          const body = await r.json().catch(() => ({}));
          if (controller.signal.aborted) return;
          const errType = body.error === "revoked" ? "revoked" : body.error === "expired" ? "expired" : body.error === "invalid" ? "invalid" : "unknown";
          setErrorType(errType);
          setErrorMsg(body.message ?? t("seg18.errUnknownTitle"));
          setStatus("error");
        }
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setErrorType("unknown"); setErrorMsg(t("seg18.networkError")); setStatus("error");
      });
    return () => controller.abort();
  }, [token]);

  const handleRespond = async (poId: string, respStatus: string, comment: string, adjustedLines: any[] | undefined, expectedRevision: number) => {
    const body: any = { poId, status: respStatus, comment, expectedRevision };
    if (adjustedLines) body.adjustedLines = adjustedLines;
    const r = await fetch(`/api/public/supplier-portal/${token}/respond`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw Object.assign(new Error(err.message ?? "Failed to submit response."), { status: r.status });
    }
    return r.json();
  };

  // Loading
  if (status === "loading") {
    return (
      <div className="light min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error states
  if (status === "error") {
    const messages: Record<string, { title: string; sub?: string }> = {
      invalid: { title: t("seg18.errInvalidTitle") },
      revoked: { title: t("seg18.errRevokedTitle") },
      expired: { title: t("seg18.errExpiredTitle"), sub: t("seg18.errExpiredSub") },
      unknown: { title: errorMsg || t("seg18.errUnknownTitle"), sub: t("seg18.errUnknownSub") },
    };
    const { title, sub } = messages[errorType] ?? messages.unknown;
    return (
      <div className="light min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center space-y-3 max-w-sm">
          <Lock className="h-12 w-12 mx-auto text-muted-foreground opacity-40" />
          <h1 className="text-xl font-semibold">{title}</h1>
          {sub && <p className="text-sm text-muted-foreground">{sub}</p>}
        </div>
      </div>
    );
  }

  // Success state
  const branches: any[] = data?.branches ?? [];
  const totals: any[] = data?.consolidatedTotals ?? [];

  return (
    <div className="light bg-gray-50 min-h-screen">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">

        {/* Header */}
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-mono">INFRASTOCK</p>
          <h1 className="text-2xl font-heading font-medium uppercase tracking-wide text-gray-900" dir="auto">
            {t("seg18.ordersFor", { name: data?.supplierName })}
          </h1>
          <p className="text-muted-foreground text-sm" dir="auto">
            {t("seg18.cycleDelivery", { cycle: data?.cycleName, date: fmtDateLong(data?.deliveryDate) })}
          </p>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            {t("seg18.secureLink", { expiry: expiryText(data?.expiresAt, t) })}
          </div>
        </div>

        {/* Consolidated totals card */}
        {totals.length > 0 && (
          <Card className="border-primary/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading font-medium uppercase tracking-wide flex items-center justify-between">
                <span>{t("seg18.totalOrders")}</span>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                  onClick={() => setExpanded(p => !p)}>
                  {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CardTitle>
            </CardHeader>
            {expanded && (
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {totals.map((item: any) => (
                    <div key={JSON.stringify([item.itemId ?? item.itemName, item.unit, item.brandId])}
                      className="p-3 bg-primary/5 rounded-lg text-center"
                      data-testid={`total-${item.itemId}`}>
                      <p className="text-xs text-muted-foreground truncate" dir="auto">{item.itemName}{poBrandLabel(item) && <small className="block">{poBrandLabel(item)}</small>}</p>
                      <p className="text-xl font-mono font-bold text-primary mt-0.5">{item.totalQty}</p>
                      <p className="text-xs text-muted-foreground">{item.unit}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Delivery date separator */}
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <span className="text-base font-heading font-medium uppercase tracking-wide text-muted-foreground text-sm whitespace-nowrap">
            📅 {fmtDateLong(data?.deliveryDate)}
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Branch cards */}
        {branches.length === 0 && (
          <div className="text-center text-muted-foreground py-8">
            <p>{t("seg18.noBranches")}</p>
          </div>
        )}
        {branches.map((branch: any) => (
          <BranchCard
            key={`${token}:${branch.branchId}`}
            branch={branch}
            showPricing={!!data?.showPricing}
            onRespond={handleRespond}
            onReload={async poId => {
              const result = await fetch(`/api/public/supplier-portal/${token}`, { cache: "no-store" });
              const body = await result.json();
              if (!result.ok) throw new Error(body.message ?? "Unable to reload this order.");
              const current = body.branches?.find((entry: any) => entry.poId === poId);
              if (!current) throw new Error("This order is no longer available through this link.");
              return current;
            }}
          />
        ))}

        {/* Footer */}
        <div className="text-center mt-8 pt-4 border-t">
          <p className="text-xs text-muted-foreground">Infrastock by Inframodern</p>
        </div>

      </div>
    </div>
  );
}
